import { ReactNode, useEffect, useState } from "react";
import { useCreateMfeSessionMutation, useLazyGetCoreApplicationContextQuery } from "@webcore/services/api/api.ts";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner.tsx";
import { CoreApplicationContextProvider } from "@webcore/context-providers/CoreApplicationContextProvider.tsx";
import { isMfeTokenMessage, mfeReadyMessageType } from "@webcore/services/mfe/mfeMessaging.ts";

type BootstrapStatus = "awaiting-token" | "establishing" | "ready" | "error";

/**
 * Reads a one-time token from the URL fragment (e.g. `#mfe_token=...`) if the parent chose
 * the fragment-handoff path rather than postMessage. The fragment is cleared immediately so
 * the token is not left in history.
 */
function consumeTokenFromFragment(): string | null {
  const hash = window.location.hash.replace(/^#/, "");

  if (hash === "") {
    return null;
  }

  const params = new URLSearchParams(hash);
  const token = params.get("mfe_token");

  if (token === null) {
    return null;
  }

  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return token;
}

/**
 * MFE bootstrap. Receives the short-lived read-only token from the parent app (postMessage,
 * with a URL-fragment fallback), POSTs it to `/api/v1/auth/mfe-session` to set a host-only
 * read-only cookie on this origin, then loads the CoreApplicationContext for that session and
 * renders its children under the shared CoreApplicationContextProvider — the same way web-client's
 * auth layer does, just at read scope. This is the MFE's "auth" layer: nothing below it renders
 * until a session cookie exists and a CoreApplicationContext (with the active team) is loaded.
 */
function MfeBootstrap({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<BootstrapStatus>("awaiting-token");
  const [createMfeSession] = useCreateMfeSessionMutation();
  const [getCoreApplicationContext] = useLazyGetCoreApplicationContextQuery();

  useEffect(() => {
    let isActive = true;

    const establishSession = async (token: string) => {
      setStatus("establishing");

      try {
        await createMfeSession({ token }).unwrap();
        // The read cookie is now set; load the CoreApplicationContext into the coreContext slice so
        // the shared provider can expose it (and the active team) to the rendered view/widget.
        await getCoreApplicationContext().unwrap();

        if (isActive) {
          setStatus("ready");
        }
      }
      catch {
        if (isActive) {
          setStatus("error");
        }
      }
    };

    const window_message = (event: MessageEvent) => {
      // Only accept the token from the parent (app) window via the agreed message shape.
      // The token itself is validated server-side, so we don't pin a single parent origin.
      if (event.source !== window.parent || !isMfeTokenMessage(event.data)) {
        return;
      }

      window.removeEventListener("message", window_message);
      void establishSession(event.data.token);
    };

    const fragmentToken = consumeTokenFromFragment();

    if (fragmentToken !== null) {
      void establishSession(fragmentToken);
    }
    else {
      window.addEventListener("message", window_message);
      // Tell the parent we are mounted and ready to receive the token.
      window.parent.postMessage({ type: mfeReadyMessageType }, "*");
    }

    return () => {
      isActive = false;
      window.removeEventListener("message", window_message);
    };
  }, [createMfeSession, getCoreApplicationContext]);

  if (status === "error") {
    return (
      <div className="p-4">
        <p className="text-danger">Could not establish a read-only session.</p>
      </div>
    );
  }

  if (status !== "ready") {
    return <FullViewSpinner />;
  }

  // The CoreApplicationContext is loaded; expose it (and the active team) to the rendered subtree.
  return <CoreApplicationContextProvider>{children}</CoreApplicationContextProvider>;
}

export default MfeBootstrap;
